import type { Mutation } from '@tanstack/react-query';
import { useEffect, useState, useSyncExternalStore } from 'react';
import styled, { keyframes } from 'styled-components';

// What a successful mutation announces. A mutation can set `meta.toast` to its own
// words, or to false when it isn't a save the admin made (an upload, a preview
// link) or its page announces something more specific itself.
export function saveMessage(
  mutation: Mutation<unknown, unknown, unknown, unknown>,
) {
  const meta = mutation.options.meta?.toast;
  if (meta === false) return null;
  if (typeof meta === 'string') return meta;
  const key = mutation.options.mutationKey?.[0] as
    | { method?: string }
    | undefined;
  return key?.method?.toLowerCase() === 'delete' ? 'Deleted' : 'Saved';
}

type Notice = { id: number; message: string };

const SHOW_MS = 4000;
let notice: Notice | null = null;
let nextId = 1;
const listeners = new Set<() => void>();

function publish(next: Notice | null) {
  notice = next;
  for (const listener of listeners) listener();
}

// One banner at a time: a newer save replaces the one on screen.
export function announceSaved(message: string) {
  const text = /[.!?]$/.test(message) ? message : `${message}.`;
  publish({ id: nextId++, message: text });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// The admin's save confirmation, like WordPress's notices: it slides in at the
// top of the page, stays in view while scrolled, then fades.
export function SaveBanner() {
  const current = useSyncExternalStore(subscribe, () => notice);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    setLeaving(false);
    if (!current) return;
    const fade = setTimeout(() => setLeaving(true), SHOW_MS);
    const clear = setTimeout(
      () => notice?.id === current.id && publish(null),
      SHOW_MS + 400,
    );
    return () => {
      clearTimeout(fade);
      clearTimeout(clear);
    };
  }, [current]);

  return (
    <Slot role="status" aria-live="polite">
      {current && (
        <Banner key={current.id} $leaving={leaving}>
          <span>
            <Check aria-hidden>✓</Check>
            {current.message}
          </span>
          <Dismiss
            type="button"
            aria-label="Dismiss"
            onClick={() => publish(null)}
          >
            ×
          </Dismiss>
        </Banner>
      )}
    </Slot>
  );
}

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-0.5rem);
  }
`;

// At the top of the page it pushes the content down, as WordPress's notices do;
// sticky, so it's still in view on a page scrolled to a save lower down.
const Slot = styled.div`
  position: sticky;
  top: 0.75rem;
  z-index: 40;
`;

const Banner = styled.div<{ $leaving: boolean }>`
  align-items: center;
  animation: ${slideIn} 200ms ease-out;
  background: color-mix(in srgb, var(--success) 12%, var(--component-color));
  border-left: 4px solid var(--success);
  border-radius: 0.25rem;
  box-shadow: var(--shadow-md);
  color: var(--grey-900);
  display: flex;
  font-size: 0.9375rem;
  font-weight: 600;
  justify-content: space-between;
  margin-bottom: var(--component-margin);
  opacity: ${(p) => (p.$leaving ? 0 : 1)};
  padding: 0.625rem 0.875rem;
  transition: opacity 400ms;
`;

const Check = styled.span`
  color: var(--success);
  margin-right: 0.5rem;
`;

const Dismiss = styled.button`
  background: none;
  border: 0;
  color: var(--grey-600);
  cursor: pointer;
  font-size: 1.125rem;
  line-height: 1;
  padding: 0 0.25rem;

  &:hover {
    color: var(--grey-900);
  }
`;
